import { z } from 'zod';
import { HIDEABLE_NAV_SECTIONS } from '../constants/nav-sections.js';
import { MAX_ALWAYS_ZERO_LIMIT, PUSH_HALT_KINDS } from '../data/push-sku-rules.js';
import { shortString, shopifyDomainSchema, emailSchema, nonNegativeInt, uuidSchema, slugSchema, } from './validation.js';
/**
 * Organization Schemas
 *
 * Shared validation schemas for organization/tenant management.
 * Organizations represent separate ThreadHeads customers in multi-tenant architecture.
 */
/**
 * Shopify Dev-Dashboard client credentials are a pair: exactly one of the two
 * being present is always a user error. Single source of truth for the rule —
 * consumed by both org schemas, the org service guard, and the connect forms.
 */
export function hasPartialShopifyClientPair(input) {
    return Boolean(input.shopifyClientId?.trim()) !== Boolean(input.shopifyClientSecret?.trim());
}
export const PARTIAL_CLIENT_PAIR_MESSAGE = 'Enter both the Client ID and the Client Secret.';
/**
 * Update organization settings
 */
export const updateOrganizationSchema = z.object({
    name: shortString.min(1, 'Organization name is required').optional(),
    shopifyDomain: shopifyDomainSchema.optional(),
    shopifyWebhookSecret: shortString.min(1, 'Webhook secret is required').optional(),
    shopifyAccessToken: shortString.optional(),
    // Dev-Dashboard app credentials: the backend exchanges these for an access
    // token (client_credentials grant) and auto-refreshes it thereafter.
    shopifyClientId: shortString.optional(),
    shopifyClientSecret: shortString.optional(),
    defaultCurrency: z.string().length(3, 'Currency code must be 3 characters (e.g., USD, AUD)').optional(),
    timezone: shortString.min(1, 'Timezone is required').optional(),
    isActive: z.boolean().optional(),
    thumbnailUrl: z.string().url().max(2000).optional().nullable(),
    // BLU-718: how long this org's new orders wait before they can be batched, so
    // the customer can still edit or cancel in Shopify. null = inherit the 2h
    // default; 0 = no hold. Bounds mirror the DB CHECK
    // (organizations_order_hold_minutes_range).
    orderHoldMinutes: z.number().int().min(0).max(1440).nullable().optional(),
    settings: z.lazy(() => organizationSettingsSchema).optional(),
}).refine((data) => !hasPartialShopifyClientPair(data), {
    message: PARTIAL_CLIENT_PAIR_MESSAGE,
    path: ['shopifyClientSecret'],
});
// Branding asset location: a full URL (Supabase Storage public URL) or a
// root-relative path to a bundled frontend asset (e.g. '/tommy-crying.svg',
// seeded for the ThreadHeads org so its pre-branding look is preserved).
const brandingAssetUrl = z.string().max(2000).refine((v) => v.startsWith('/') || z.string().url().safeParse(v).success, { message: 'Must be a URL or a root-relative path' });
/**
 * Organization settings (general configuration)
 */
export const organizationSettingsSchema = z.object({
    // Org-managed brand names — powers an autocomplete on Product Dev's
    // free-text `brand` grouping field (apps/backend/src/routers/product-dev.ts).
    // Not a foreign key: operators maintain their own list via Settings. No
    // seed — orgs enter their own. `.optional()` (not `.default([])`) so the
    // inferred type matches every sibling settings block and the repo-wide
    // `settings ?? {}` spread pattern keeps compiling; readers do `?? []`.
    brands: z.array(z.string().trim().min(1).max(120)).max(50).optional(),
    /**
     * Sidebar sections this org has hidden from its own nav — COSMETIC ONLY.
     *
     * ⚠ Not a permission: the routes stay reachable by URL and the endpoints
     * still serve. See HIDEABLE_NAV_SECTIONS for the full rationale and the
     * reason Settings and the workspace home can never be hidden.
     *
     * `.optional()` (not `.default([])`) to match every sibling block and keep
     * the repo-wide `settings ?? {}` spread compiling; readers do `?? []`.
     */
    hiddenNavSections: z.array(z.enum(HIDEABLE_NAV_SECTIONS)).optional(),
    notifications: z.object({
        lowStockEnabled: z.boolean().default(true),
        orderReceivedEnabled: z.boolean().default(true),
        emailRecipients: z.array(emailSchema).optional(),
    }).optional(),
    inventory: z.object({
        defaultNotifyThreshold: nonNegativeInt.default(10),
        allowNegativeStock: z.boolean().default(false),
    }).optional(),
    orders: z.object({
        autoTransitDays: nonNegativeInt.optional(),
        requireApproval: z.boolean().default(false),
    }).optional(),
    purchaseOrders: z.object({
        numberPrefix: shortString.optional(),
        numberFormat: shortString.optional(),
        numberPadding: z.number().int().min(1).max(10).optional(),
        draftPrefix: shortString.optional(),
        senderEmail: emailSchema.optional(),
        replyToEmail: emailSchema.optional(),
        internalCcEmail: emailSchema.optional(),
        // BLU-1268: the Xero account a just-in-time PO's bill lines go to (a cost
        // of goods or expense account, never the inventory asset account). Unset =
        // the just-in-time bill push refuses. Settings saves strip unknown keys, so
        // it must live in this schema.
        justInTimeAccountCode: z.string().trim().max(20).optional(),
    }).optional(),
    // BLU-495 Q4: `availability` block removed. The back-sync worker that
    // consumed it is deleted. Legacy `availability` JSONB on existing org rows
    // is silently stripped by Zod's default `.strip()` on settings save —
    // harmless since no reader exists post-Q4. The DB column retention is
    // documented in supabase/migrations/20260525000000_drop_availability_trigger.sql.
    company: z.object({
        name: z.string().max(200).optional(),
        address: z.string().max(500).optional(),
        logoUrl: z.string().url().max(2000).optional(),
    }).optional(),
    // White-label app branding: drives the sidebar logo, app loading screen,
    // document.title, and the catcher redo-dialog mascot. Rendered via
    // resolveBranding() (apps/frontend/src/lib/branding.ts) with ThreadHeads
    // defaults when unset. MUST stay in this schema — settings saves Zod-strip
    // unknown keys. Asset fields accept a full URL (Supabase Storage upload) OR
    // a root-relative path — the ThreadHeads seed migration stores bundled
    // frontend assets like '/tommy-crying.svg'.
    //
    // Two surface-specific logos (each designed for the background it sits on):
    //  - logoUrl      → sidebar, a DARK surface  → use a light-coloured logo.
    //  - logoLightUrl → app loading screen, a WHITE surface → use a dark logo.
    // logoLightUrl falls back to logoUrl (see resolveBranding) so an org that
    // only ever set logoUrl keeps its single-logo behaviour on both surfaces.
    branding: z.object({
        displayName: z.string().trim().max(100).optional(),
        logoUrl: brandingAssetUrl.optional(),
        logoLightUrl: brandingAssetUrl.optional(),
        mascotUrl: brandingAssetUrl.optional(),
        // Schema-ready for phase 2; not rendered anywhere yet.
        faviconUrl: brandingAssetUrl.optional(),
        primaryColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    }).optional(),
    inventoryPush: z.object({
        enabled: z.boolean().default(false),
        autoSyncEnabled: z.boolean().default(false),
        dryRunMode: z.boolean().default(true),
        seedInventory: z.boolean().default(true),
        intervalMinutes: z.number().int().min(5).max(1440).default(5),
        safetyBuffer: z.number().int().min(0).max(100).default(0),
        // BLU-495 Q4: percent-of-notify_threshold safety buffer. Coexists with
        // absolute `safetyBuffer` — effectiveBuffer = max(absolute, ceil(notify_threshold × pct / 100)).
        // null = off. See inventory-push.worker.ts `computeEffectiveSafetyBuffer`.
        safetyBufferPercent: z.number().int().min(0).max(100).nullable().default(null),
        zeroStockThreshold: z.number().int().min(0).max(500).default(10),
        // BLU-1541: most Always 0 SKUs one scheduled push may take to 0 before it
        // halts. 0 = No limit. No default on purpose: absent reads as 200 (see
        // `alwaysZeroCeiling`), so an org that never touched it keeps the default.
        alwaysZeroLimit: z.number().int().min(0).max(MAX_ALWAYS_ZERO_LIMIT).optional(),
        approvalEnabled: z.boolean().default(false),
        dropUnitsThreshold: z.number().int().min(1).max(10000).default(50),
        dropPercentThreshold: z.number().int().min(1).max(100).default(80),
        zeroOutProtection: z.boolean().default(true),
        // BLU-553: per-SKU downward-push blocker. Independent of the Approval
        // Queue above (applies on EVERY push cycle, scheduled AND manual — not
        // bypassed by "Push Now"). Excludes only the offending SKU from the batch
        // and queues a shopify_pending_push_changes row (triggered_rule
        // 'delta_block'); approving it pushes that SKU directly. See
        // inventory-push.worker.ts `evaluateDeltaBlock` / `applyDeltaBlockGuard`.
        deltaBlockEnabled: z.boolean().default(true),
        deltaBlockThreshold: z.number().int().min(1).max(1000000).default(5000),
        discoveryHourUtc: z.number().int().min(0).max(23).nullable().default(null),
        // First Sync Audit: new POD variants discovered per push cycle. Exposes
        // the previously env-only MAX_NEW_DISCOVERIES_PER_CYCLE as a per-org
        // setting. null = use the env default (inventory-push.worker.ts).
        discoveriesPerCycle: z.number().int().min(1).max(25).nullable().default(null),
        autoEnableTracking: z.boolean().default(false),
        autoStockAtLocation: z.boolean().default(true),
        // Product Sync approval is the inventory-push authorization boundary.
        // Existing organizations are explicitly backfilled to true by the
        // lifecycle migration; newly-created settings use the safer false default.
        pushApprovedDraftProducts: z.boolean().default(false),
        // Periodically inspect recently activated Shopify products and enqueue
        // inventory coverage/push work for Product Sync-approved variants.
        // Existing organizations are explicitly backfilled off; new orgs opt in.
        autoDiscoverNewActiveProducts: z.boolean().default(true),
        // Hidden rollout seam: authorization remains enforced even when lifecycle
        // discovery is disabled. Existing orgs receive an explicit false migration
        // backfill; missing/new settings fail closed.
        requireProductSyncInventoryApproval: z.boolean().default(true),
        // BLU-677: per-event push accelerators. Each fires an immediate push right
        // after a stock-changing event instead of waiting for the scheduled cycle.
        // ALL default false — opt-in per org. NOTE: this makes PO-receive OFF by
        // default, a change from BLU-644's always-on behavior; orgs relying on
        // instant PO-receive push must toggle `pushOnPurchaseOrderReceive` on.
        pushOnPurchaseOrderReceive: z.boolean().default(false),
        pushOnTransferReceive: z.boolean().default(false),
        pushOnStockAdjustment: z.boolean().default(false),
        pushOnStocktake: z.boolean().default(false),
        // When on, the push sets inventoryPolicy=DENY on any POD variant it discovers
        // that is still "continue selling", so qty 0 actually becomes unsellable.
        // Default off. Existing-backlog flips are done by `npm run audit:shopify-inventory-policy`.
        enforceDenyPolicy: z.boolean().default(false),
        // BLU-354c: legacy `hybridQty` + `hybridLocationIds` removed from schema.
        // Hybrid (PAP + blank) push is controlled by
        // `locations.production_settings.hybrid_fulfillment_mode` — the same
        // per-location toggle that drives production-side PAP→blank reverts.
        // Single source of truth; see `HybridFulfillmentSettingsSection.tsx`.
        // Migration 20260422000000 strips old fields from existing rows.
        halted: z.object({
            // BLU-1541: which guard halted; absent on halts written before it existed.
            kind: z.enum(PUSH_HALT_KINDS).optional(),
            reason: z.string(),
            skuCount: z.number(),
            detectedAt: z.string(),
            skuSamples: z.array(z.object({
                sku: z.string(),
                previousQty: z.number(),
                newQty: z.number(),
            })),
        }).nullable().optional(),
        notifications: z.object({
            slackWebhookUrl: z.string().url().max(500)
                .refine((v) => {
                try {
                    const hostname = new URL(v).hostname;
                    return hostname === 'hooks.slack.com' || hostname === 'hooks.slack-gov.com';
                }
                catch {
                    return false;
                }
            }, { message: 'Must be a Slack webhook URL (hooks.slack.com or hooks.slack-gov.com)' })
                .nullable().default(null),
            slackEnabled: z.boolean().default(false),
            notifyOnHalt: z.boolean().default(true),
            notifyOnPendingApproval: z.boolean().default(true),
            notifyOnPushError: z.boolean().default(true),
            notifyOnPushSummary: z.boolean().default(false),
            // ---- PO daily digests (po-notifications) -----------------------------
            // Written by the Notifications page (settingsShared.NotificationSettings).
            // Without them here, Zod's default `.strip()` silently dropped these keys
            // on every settings save, resetting a user's "off" preference to default.
            notifyOnPoBehindPace: z.boolean().default(true),
            notifyOnPoStatusUpdates: z.boolean().default(true),
            // ---- Weekly key-item stock digest (BLU OOS digest) -------------------
            // A Tuesday roll-up of starred SKUs in the ERP's red/yellow stock bands,
            // posted via
            // the Threadbot bot token to a chosen channel (NOT the incoming webhook
            // above — a webhook is bound to one channel and can't be picked). See
            // `oos-notifications` module.
            notifyOnOosDigest: z.boolean().default(false),
            // Slack channel ID (preferred) or name the digest posts to. Resolved to
            // an ID at send time via `resolveChannelId`. Not a webhook URL.
            oosDigestChannel: z.string().max(200).nullable().default(null),
            // Legacy mode is retained so existing settings round-trip without data
            // loss. The weekly digest itself is intentionally always starred items.
            oosDigestMode: z.enum(['key_items', 'all_items', 'threshold']).default('key_items'),
            // Active members selected for an @mention in the digest channel. The
            // sender re-validates every UUID against this organization before use.
            oosDigestRecipientIds: z.array(uuidSchema).max(25).default([]),
            // POD-printed-ahead stock is normally noise in a replenishment digest.
            oosDigestExcludePreprinted: z.boolean().default(true),
            // BLU-537: daily Stock Mismatch (Shopify↔ERP reconcile) digest. Default
            // ON — it is the reconcile feature's primary surface.
            notifyOnReconcileSummary: z.boolean().default(true),
            // ---- Weekly pipeline digest (pipeline-digest module) -----------------
            // Finn-style Monday roll-up: Bulk/Blanks POs counted by phase + Product
            // Dev styles still in development. Posts via Threadbot to a chosen channel.
            notifyOnPipelineDigest: z.boolean().default(false),
            pipelineDigestChannel: z.string().max(200).nullable().default(null),
            // ---- Weekly hustle digest (hustle-digest module) -----------------------
            // The nag: overdue Product Dev styles (expected date past, not
            // approved/rejected/on_hold), stale styles (no update in
            // hustleDigestStaleDays), and overdue POs (eta past, not received) —
            // each deep-linked so "why is this still sitting here" happens in the
            // channel. Posts via Threadbot to a chosen channel.
            notifyOnHustleDigest: z.boolean().default(false),
            hustleDigestChannel: z.string().max(200).nullable().default(null),
            hustleDigestStaleDays: z.number().int().min(1).max(90).default(14),
            // ---- Daily push digest (push-digest module) ---------------------------
            // A once-a-day roll-up of Shopify inventory-push activity (totals,
            // per-location breakdown, errors). Replaces the per-cycle Push Summary
            // webhook toggle above. Posts via Threadbot to a chosen channel.
            notifyOnPushDigest: z.boolean().default(false),
            pushDigestChannel: z.string().max(200).nullable().default(null),
            // ---- Production digest (production-digest module) ---------------------
            // Daily roll-up of the previous AEST day's production activity (orders
            // completed, units printed/packed, redos) + an optional Monday weekly
            // roll-up with week-over-week deltas. One shared channel for both
            // cadences. Posts via Threadbot to a chosen channel.
            notifyOnProductionDaily: z.boolean().default(false),
            notifyOnProductionWeekly: z.boolean().default(false),
            productionDigestChannel: z.string().max(200).nullable().default(null),
            // ---- Incoming shipments digest (incoming-shipments-digest module) -----
            // Monday roll-up of in-transit / partially-received POs grouped by
            // destination location ("what's landing this week, where").
            notifyOnIncomingShipments: z.boolean().default(false),
            incomingShipmentsChannel: z.string().max(200).nullable().default(null),
            // ---- Partner portal: PO-submitted notify (supplier-org side) ----------
            // Read on the SUPPLIER org when a buyer submits a purchase order whose
            // supplier record has an active partner junction. Delivered via the
            // supplier org's OWN Threadbot to the chosen channel (cross-org notify,
            // per-org Slack identity). See supplier-portal/partner-notify.ts.
            notifyOnPartnerPoSubmit: z.boolean().default(false),
            partnerPoSubmitChannel: z.string().max(200).nullable().default(null),
            // ---- Product Dev collaboration updates (product-dev-slack-notifier) ---
            // Per-event Slack posts to a chosen channel when a Product Dev record a
            // partner (e.g. Thread Lab) collaborates on changes: samples requested/
            // received/approved, stage/status changes, file uploads, new comments.
            // Posted via THIS org's own Threadbot to a channel it also shares with
            // the partner (owner-org identity — no cross-org credential hop). Master
            // toggle + channel + per-category toggles so an operator routes exactly
            // what it wants. Multi-tenant: every org configures its own channel/bot.
            notifyOnProductDevUpdates: z.boolean().default(false),
            productDevUpdatesChannel: z.string().max(200).nullable().default(null),
            notifyOnProductDevSamples: z.boolean().default(true),
            notifyOnProductDevStages: z.boolean().default(true),
            // Comments + files default OFF: enabling the feature must not silently
            // broadcast internal buyer-side discussion / every file to a
            // partner-shared channel. Operators opt into these deliberately.
            notifyOnProductDevComments: z.boolean().default(false),
            notifyOnProductDevFiles: z.boolean().default(false),
            // ---- Stocktake proposal messages (stocktake-messages module, BLU-711 A2) ---
            // A message on a stocktake proposal thread is ONLY ever a link ping here —
            // never the body, never a stock figure (handover §7 "Messages never leave
            // the app"). Posted via the POSTING (sender) org's OWN Threadbot to a
            // channel it shares with the counterparty (owner-org identity, same model
            // as notifyOnProductDevUpdates above — every org configures its own
            // channel/bot, and each side's ping uses its own settings when IT posts).
            // `notifyOnStocktakeMessages` is what the notifier actually reads; it is
            // kept in lockstep with `stocktakeMessagesNotifyMode` below (mode='slack'
            // implies true, 'none'/'copy' imply false) by the stocktakeMessages
            // router's setNotifyConfig — never set independently by a form.
            notifyOnStocktakeMessages: z.boolean().default(false),
            stocktakeMessagesChannel: z.string().max(200).nullable().default(null),
            // The gear dialog's own last-selected option, so it round-trips even
            // though 'copy' (client-side clipboard) has no Slack-side effect.
            stocktakeMessagesNotifyMode: z.enum(['none', 'slack', 'copy']).default('none'),
        }).optional(),
        // BLU-537: daily Shopify↔ERP inventory reconcile (Stock Mismatch cron).
        // MUST live in this schema — settings saves Zod-strip unknown keys, so an
        // ops-set blob outside the schema silently reverts to defaults on the next
        // Save click. Defaults here must agree with `parseReconcileConfig`
        // (apps/backend/src/modules/shopify/inventory-reconcile.ts).
        reconcile: z.object({
            enabled: z.boolean().default(true),
            // 'report' = digest only; 'fix' = force-correct drift via pushSingleSku.
            mode: z.enum(['report', 'fix']).default('report'),
            toleranceUnits: z.number().int().min(0).max(1000).default(0),
            maxCorrections: z.number().int().min(1).max(10000).default(200),
            // Fix-mode value-sanity rail: never push an SKU whose expected quantity
            // exceeds this — absurd values are corrupt/sentinel ERP data, not stock.
            maxSaneExpectedQty: z.number().int().min(1000).max(10_000_000).default(500_000),
            // BLU-540: ADDITIVELY refresh the push-state variant cache from the
            // nightly bulk read — supersedes paged pre-discovery for ENUMERATION
            // ONLY. Pre-discovery (`discoveryHourUtc`) is still required for
            // auto-enabling untracked variants, location activation and DENY
            // enforcement; this refresh only sees tracked variants.
            refreshVariantCache: z.boolean().default(true),
            // Fix mode: zero Shopify levels for ERP-managed SKUs at ERP-mapped
            // locations with NO sku_inventory row (push-to-all-locations residue).
            // Only ever writes 0.
            //
            // BLU-714: DEFAULTS TO FALSE, and must stay that way until BLU-715's
            // per-location `erp_authoritative_for_stock` gate ships. Zeroing acts on
            // the ABSENCE of an ERP row, which is exactly what a broken location map
            // manufactures. The writes bypass `pushSingleSku`, but are guarded by a
            // last-moment ERP/authority re-read and Shopify compare-and-set; the
            // `last_pushed_qty` cache remains untouched.
            // Effectively one-way, at up to MAX_ZEROED_LEVELS (10k) per night.
            // This default previously read `true` here AND `!== false` in
            // parseReconcileConfig, so a settings save that dropped the key silently
            // re-armed the whole pass.
            zeroUnstockedLocations: z.boolean().default(false),
            // BLU-550 opt-in: replace the hybrid-location exclusion in
            // selectPhantomLevels with the per-row predicate. Declared here so a
            // settings save cannot silently strip it mid-drain, leaving
            // `zeroedAtHybrid: 0` as the only (indistinguishable) signal.
            zeroPhantomAtHybrid: z.boolean().default(false),
            // BLU-550: evict cross-blank ids from the push-state variant cache on a
            // complete read. Declared for the same reason — it was enabled on prod
            // 2026-06-30 and is ABSENT today, which is this bug having already fired.
            pruneCrossBlankCache: z.boolean().default(false),
        }).optional(),
        // BLU-1540: Push Exclusions (location ids). Never declared before, so the
        // parse stripped it on every save and no exclusion change from the
        // settings screen ever stuck. No default on purpose: an absent key means
        // "caller didn't send it, keep the stored list", while an explicit []
        // means "clear it" (see organization.service merge). Per-SKU Skip /
        // Always 0 are NOT settings: they live on `organizations.push_sku_rules`
        // (BLU-1541), so a stale `excludedSkuIds` in a payload is stripped here.
        excludedLocationIds: z.array(z.string().min(1).max(64)).max(500).optional(),
    }).optional(),
    stockAlerts: z.object({
        enabled: z.boolean().default(false),
        slackWebhookUrl: z.string().url().max(500)
            .refine((v) => {
            try {
                const hostname = new URL(v).hostname;
                return hostname === 'hooks.slack.com' || hostname === 'hooks.slack-gov.com';
            }
            catch {
                return false;
            }
        }, { message: 'Must be a Slack webhook URL (hooks.slack.com or hooks.slack-gov.com)' })
            .nullable().default(null),
        cooldownHours: z.number().int().min(1).max(168).default(4),
        alertMode: z.enum(['key_items', 'all_items', 'threshold']).default('key_items'),
    }).optional(),
    papSync: z.object({
        status: z.enum(['running', 'completed', 'failed']).optional(),
        startedAt: z.string().optional(),
        lastCompletedAt: z.string().nullable().optional(),
        progress: z.object({
            pagesProcessed: z.number(),
            productsProcessed: z.number(),
            skusUpserted: z.number(),
        }).optional(),
        error: z.string().nullable().optional(),
        source: z.string().optional(),
        productType: z.string().nullable().optional(),
        // Legacy flat fields (pre-progress tracking)
        productsProcessed: z.number().optional(),
        skusUpserted: z.number().optional(),
    }).optional(),
});
/**
 * Get organization details
 */
export const getOrganizationSchema = z.object({
    includeSettings: z.boolean().default(false).optional(),
    includeLocations: z.boolean().default(false).optional(),
    includeUsers: z.boolean().default(false).optional(),
});
/**
 * Update organization settings (partial update)
 */
export const updateOrganizationSettingsSchema = z.object({
    settings: organizationSettingsSchema,
});
export const createOrganizationSchema = z
    .object({
    organizationName: shortString.min(1, 'Organization name is required'),
    slug: slugSchema,
    // P2: Shopify is optional at creation — a true-3PL client org (e.g. Mini
    // Zali) is created first and connects Shopify later via Settings →
    // Integrations. Mirrors updateOrganizationSchema (already optional).
    // Normalize '' → undefined so a create form that transmits empty strings for
    // the blank Shopify section is accepted (`.optional()` alone rejects '' via
    // the base validator before the both-or-neither refine runs).
    shopifyDomain: z.preprocess((v) => (v === '' ? undefined : v), shopifyDomainSchema.optional()),
    shopifyWebhookSecret: shortString.min(1, 'Webhook secret is required').optional(),
    shopifyAccessToken: z.preprocess((v) => (v === '' ? undefined : v), shortString.optional()),
    // Dev-Dashboard app credentials — alternative to shopifyAccessToken. The
    // backend mints the access token via the client_credentials grant.
    shopifyClientId: z.preprocess((v) => (v === '' ? undefined : v), shortString.optional()),
    shopifyClientSecret: z.preprocess((v) => (v === '' ? undefined : v), shortString.optional()),
    adminEmail: emailSchema,
    // P1: a fresh dedicated Admin is always minted, so a password is required.
    adminPassword: shortString.min(8, 'Password must be at least 8 characters'),
    adminFirstName: shortString.min(1, 'Admin first name is required'),
    adminLastName: shortString.min(1, 'Admin last name is required'),
})
    // P2: Shopify config is all-or-nothing. A domain needs credentials to
    // authenticate — either an access token OR a Client ID + Secret pair — and
    // credentials without a domain have nothing to talk to.
    .refine((data) => {
    const hasCreds = Boolean(data.shopifyAccessToken) ||
        (Boolean(data.shopifyClientId) && Boolean(data.shopifyClientSecret));
    return !hasPartialShopifyClientPair(data) && Boolean(data.shopifyDomain) === hasCreds;
}, {
    message: 'Provide a Shopify domain plus either a Client ID and Client Secret (recommended) or an access token, or leave all blank to connect Shopify later.',
    path: ['shopifyAccessToken'],
});
export const organizationMemberSchema = z.object({
    id: uuidSchema,
    first_name: shortString.optional(),
    last_name: shortString.optional(),
    email: emailSchema,
    is_active: z.boolean().optional(),
});
/**
 * Run manual PAP product sync
 */
export const runPapProductSyncSchema = z.object({
    productType: z.string().min(1).optional(),
    syncRange: z.enum(['full', 'since_last', '7d', '30d', '90d']).optional(),
});
/**
 * Run manual Shopify sync
 */
export const runSyncSchema = z.object({
    lookbackMinutes: z.number().int().min(1).max(60 * 24 * 30).optional(),
});
/**
 * Manual sync result - returned when job starts
 */
export const syncResultSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    startedAt: z.string(), // ISO timestamp
    organizationId: uuidSchema,
    organizationName: z.string(),
});
/**
 * Backfill Shopify inventory item IDs for SKUs
 */
export const backfillInventorySchema = z.object({
    batchSize: z.number().int().min(1).max(500).optional(),
});
export const backfillUnmatchedSkusSchema = z.object({
    locationId: z.string().uuid().nullable().optional(),
    batchSize: z.number().int().min(100).max(2000).optional(),
    concurrency: z.number().int().min(1).max(16).optional(),
});
export const inventoryBackfillResultSchema = z.object({
    success: z.boolean(),
    processedCount: z.number(),
    updatedCount: z.number(),
    remaining: z.number(),
    startedAt: z.string(),
});
export const backfillUnmatchedSkusResultSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    startedAt: z.string(),
});
/**
 * Get master inventory report - all SKUs across all locations with sales data
 */
export const getMasterInventoryReportSchema = z.object({
// No input required - uses current organization from auth context
});
/**
 * Master inventory report row - one row per SKU per location
 */
export const masterInventoryReportRowSchema = z.object({
    sku: z.string(),
    location_name: z.string(),
    production_type: z.string().nullable(),
    type: z.string().nullable(),
    color: z.string().nullable(),
    size: z.string().nullable(),
    on_hand: z.number(),
    available: z.number(),
    committed: z.number(),
    unavailable: z.number(),
    incoming: z.number(),
    /**
     * BLU-1268: the pair's supply mode. A just_in_time row has no stock number:
     * on_hand and available read 9999 (what the store is told), the rest 0.
     * Absent reads as stocked.
     */
    stock_mode: z.enum(['stocked', 'just_in_time', 'managed_elsewhere']).optional(),
    total_sold_30d: z.number().default(0),
    unique_orders_30d: z.number().default(0),
    sales_velocity: z.number().default(0),
});
